import React, { useState, useEffect } from 'react';
import {
  FaUsers, FaCalendarDay, FaCalendarAlt, FaCalendar, FaEnvelope,
  FaDesktop, FaMobileAlt, FaTabletAlt, FaClock, FaHistory, FaCalendarWeek,
  FaGlobeEurope, FaMapMarkerAlt, FaExternalLinkAlt, FaChartBar,
} from 'react-icons/fa';
import { authFetch } from './authHelper';

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
};

const maskIP = (ip) => {
  if (!ip) return '-';
  const parts = ip.split('.');
  if (parts.length === 4) return `${parts[0]}.${parts[1]}.xxx.xxx`;
  return ip.replace(/:[\da-f]+:[\da-f]+$/i, ':xxxx:xxxx');
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch('/api/admin/stats.php')
      .then((res) => res.json())
      .then((data) => { setStats(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="admin-loading">Chargement des statistiques...</div>;
  if (!stats) return <div className="admin-loading">Impossible de charger les statistiques.</div>;

  const deviceIcon = (type) => {
    if (type === 'mobile') return <FaMobileAlt />;
    if (type === 'tablet') return <FaTabletAlt />;
    return <FaDesktop />;
  };

  const maxDaily = Math.max(...(stats.daily_visits || []).map((d) => d.unique_visitors || 0), 1);

  return (
    <div>
      <h2 className="admin-page-title">Tableau de bord</h2>

      {/* ── Stat Cards ── */}
      <div className="admin-stats-grid-6">
        <div className="admin-stat-card">
          <div className="admin-stat-icon"><FaCalendarDay /></div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.unique_today}</span>
            <span className="admin-stat-label">Aujourd'hui</span>
            <span className="admin-stat-secondary">{stats.today_pageviews} pages vues</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: 'rgba(0,206,201,0.1)', color: 'var(--accent)' }}><FaHistory /></div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.unique_yesterday}</span>
            <span className="admin-stat-label">Hier</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: 'rgba(253,121,168,0.1)', color: '#fd79a8' }}><FaCalendarWeek /></div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.unique_7days}</span>
            <span className="admin-stat-label">7 derniers jours</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: 'rgba(253,203,110,0.1)', color: '#fdcb6e' }}><FaCalendarAlt /></div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.unique_month}</span>
            <span className="admin-stat-label">Ce mois</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: 'rgba(116,185,255,0.1)', color: '#74b9ff' }}><FaCalendar /></div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.unique_year}</span>
            <span className="admin-stat-label">Cette ann&eacute;e</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: 'rgba(255,107,107,0.1)', color: '#ff6b6b' }}><FaEnvelope /></div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.unread_messages}</span>
            <span className="admin-stat-label">Messages non lus</span>
          </div>
        </div>
      </div>

      {/* ── Duration + Total Banner ── */}
      <div className="admin-duration-card">
        <div>
          <FaClock style={{ fontSize: '1.4rem', color: 'var(--accent)' }} />
        </div>
        <div>
          <span className="admin-duration-value">{formatDuration(stats.avg_duration)}</span>
          <span className="admin-duration-label"> dur&eacute;e moyenne par visite</span>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <span style={{ fontSize: '1.4rem', fontWeight: 800 }}>{stats.unique_total}</span>
          <span className="admin-duration-label"> visiteurs uniques total</span>
          <span className="admin-stat-secondary" style={{ display: 'block' }}>{stats.total_pageviews} pages vues au total</span>
        </div>
      </div>

      {/* ── 30 Days Chart ── */}
      {stats.daily_visits && stats.daily_visits.length > 0 && (
        <div className="admin-card" style={{ marginBottom: '24px' }}>
          <h3><FaChartBar style={{ marginRight: '8px' }} />Visites des 30 derniers jours</h3>
          <div className="admin-chart-bars">
            {stats.daily_visits.map((d, i) => (
              <div key={i} className="admin-chart-bar-col" title={`${d.date} : ${d.unique_visitors} visiteurs / ${d.pageviews} pages`}>
                <div
                  className="admin-chart-bar"
                  style={{ height: `${Math.max((d.unique_visitors / maxDaily) * 100, 3)}%` }}
                />
                <span className="admin-chart-label">{d.date.slice(8)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Recent Visitors Table ── */}
      <div className="admin-card" style={{ marginBottom: '24px' }}>
        <h3><FaUsers style={{ marginRight: '8px' }} />Visiteurs r&eacute;cents</h3>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>IP</th>
                <th>Ville</th>
                <th>Pays</th>
                <th>Page</th>
                <th>Appareil</th>
                <th>Date</th>
                <th>Dur&eacute;e</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_visitors && stats.recent_visitors.length > 0 ? (
                stats.recent_visitors.map((v, i) => (
                  <tr key={i}>
                    <td className="ip-cell">{maskIP(v.ip_address)}</td>
                    <td>{v.city || '-'}</td>
                    <td>{v.country || '-'}</td>
                    <td>{v.page || '/'}</td>
                    <td>{deviceIcon(v.device_type)} {v.device_type}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(v.visited_at)}</td>
                    <td className="duration-cell">{formatDuration(v.duration_seconds)}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" className="admin-empty">Aucun visiteur</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Panels Grid ── */}
      <div className="admin-grid-2">
        <div className="admin-card">
          <h3><FaDesktop style={{ marginRight: '8px' }} />Appareils</h3>
          {stats.devices && stats.devices.length > 0 ? (
            <div className="admin-list">
              {stats.devices.map((d, i) => (
                <div key={i} className="admin-list-item">
                  <span>{deviceIcon(d.device_type)} {d.device_type || 'Inconnu'}</span>
                  <span><span className="admin-badge">{d.unique_count}</span> <span className="admin-stat-secondary">{d.total_count} pages</span></span>
                </div>
              ))}
            </div>
          ) : <p className="admin-empty">Aucune donn&eacute;e</p>}
        </div>

        <div className="admin-card">
          <h3><FaChartBar style={{ marginRight: '8px' }} />Pages les plus visit&eacute;es</h3>
          {stats.pages && stats.pages.length > 0 ? (
            <div className="admin-list">
              {stats.pages.map((p, i) => (
                <div key={i} className="admin-list-item">
                  <span>{p.page || '/'}</span>
                  <span>
                    <span className="admin-badge">{p.unique_count}</span>
                    <span className="admin-stat-secondary"> {p.total_count} vues</span>
                    {p.avg_duration && <span className="admin-stat-secondary"> &middot; {formatDuration(p.avg_duration)}</span>}
                  </span>
                </div>
              ))}
            </div>
          ) : <p className="admin-empty">Aucune donn&eacute;e</p>}
        </div>

        <div className="admin-card">
          <h3><FaGlobeEurope style={{ marginRight: '8px' }} />Pays</h3>
          {stats.countries && stats.countries.length > 0 ? (
            <div className="admin-list">
              {stats.countries.map((c, i) => (
                <div key={i} className="admin-list-item">
                  <span>{c.country}</span>
                  <span className="admin-badge">{c.unique_count}</span>
                </div>
              ))}
            </div>
          ) : <p className="admin-empty">Aucune donn&eacute;e</p>}
        </div>

        <div className="admin-card">
          <h3><FaMapMarkerAlt style={{ marginRight: '8px' }} />Villes</h3>
          {stats.cities && stats.cities.length > 0 ? (
            <div className="admin-list">
              {stats.cities.map((c, i) => (
                <div key={i} className="admin-list-item">
                  <span>{c.city || 'Inconnue'} <span className="admin-stat-secondary">{c.country}</span></span>
                  <span className="admin-badge">{c.unique_count}</span>
                </div>
              ))}
            </div>
          ) : <p className="admin-empty">Aucune donn&eacute;e</p>}
        </div>

        <div className="admin-card">
          <h3><FaExternalLinkAlt style={{ marginRight: '8px' }} />Sources de trafic</h3>
          {stats.referrers && stats.referrers.length > 0 ? (
            <div className="admin-list">
              {stats.referrers.map((r, i) => (
                <div key={i} className="admin-list-item">
                  <span>{r.referrer}</span>
                  <span className="admin-badge">{r.unique_count}</span>
                </div>
              ))}
            </div>
          ) : <p className="admin-empty">Aucune donn&eacute;e</p>}
        </div>

        <div className="admin-card">
          <h3><FaClock style={{ marginRight: '8px' }} />Dur&eacute;e moyenne par page</h3>
          {stats.page_durations && stats.page_durations.length > 0 ? (
            <div className="admin-list">
              {stats.page_durations.map((p, i) => (
                <div key={i} className="admin-list-item">
                  <span>{p.page || '/'}</span>
                  <span className="admin-badge">{formatDuration(p.avg_duration)}</span>
                </div>
              ))}
            </div>
          ) : <p className="admin-empty">Pas encore de donn&eacute;es de dur&eacute;e</p>}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
